import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, Input, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import * as echarts from 'echarts';

import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';
import { buildCategoryFilterData, matchValue, MatchMode } from '../../utils/filter.utils';

type SeriesMode = 'bar' | 'line';

interface MixedSeriesConfig {
  mode: SeriesMode;
  name: string;
  valueField: string;
  dimensionField: string;
  dimensionValue: string | number;
  yAxisIndex?: number;
}

interface NormalizedInputResult {
  categories: string[];
  recordsByCategory: Record<string, any[]>;
  categoryMetaByCategory: Record<string, { quarter: string; year: string }>;
  isFlatArrayFormat: boolean;
  metaInfo: {
    totalRecords?: number;
    totalPages?: number;
    currentPage?: number;
    pageSize?: number;
  };
}

import { CapitalCasePipe } from '../../pipes/capital-case.pipe';

@Component({
  selector: 'mbar-mline',
  standalone: true,
  imports: [CommonModule, CapitalCasePipe, CapitalCasePipe],
  templateUrl: './mbar-mline.html',
  styleUrls: ['./mbar-mline.css'],
})
export class MbarMline implements AfterViewInit, OnDestroy {
  @Input() data: any;
  metaInfo: { totalRecords?: number; totalPages?: number; currentPage?: number; pageSize?: number } = {};

  protected myChart!: echarts.ECharts;
  private filterSubscription!: Subscription;
  private resizeObserver: ResizeObserver | null = null;
  protected responsiveLayoutService = inject(ResponsiveLayoutService);
  private themeService = inject(ThemeService);
  protected reflowSub?: Subscription;
  private themeSub?: Subscription;
  private lastChartData?: any;
  private selectedCategories: string[] = [];
  private selectedLegends: string[] = [];
  private allCategories: string[] = [];

  constructor(
    protected configService: ConfigService,
    protected globalFilterService: GlobalFilterService,
    protected chartFilterHandler: ChartFilterHandlerService,
  ) {
    this.filterSubscription = this.globalFilterService.filter$.subscribe((filters) => {
      if (!this.data?.widgetId) return;

      if (!this.chartFilterHandler.shouldReload(filters, this.data.widgetId)) {
        if (filters?.legendSync) {
          this.handleExternalLegendSync(filters);
        }
        return;
      }

      if (filters?.legendSync) {
        this.handleExternalLegendSync(filters);
      }

      if (!filters?.widgetId) {
        this.selectedCategories = [];
      }

      if (!filters?.legendSync) {
        this.loadAndRenderChart(filters);
      }
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.loadAndRenderChart(), 0);

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.myChart?.resize();
    });

    // Re-render with fresh theme tokens when the user switches theme at runtime.
    // Dispose + re-init so ECharts picks up the updated 'dvm' registered theme.
    this.themeSub = this.themeService.theme$.subscribe(() => {
      if (!this.lastChartData || !this.myChart) return;
      const chartDom = document.getElementById(this.data?.widgetId || '');
      if (!chartDom) return;
      this.myChart.dispose();
      this.myChart = echarts.init(chartDom, 'dvm');
      this.initChart(this.lastChartData);
    });
  }

  protected loadAndRenderChart(filters?: any): void {
    const chartDom = document.getElementById(this.data?.widgetId || '');
    if (!chartDom) {
      console.warn('Chart DOM not found:', this.data?.widgetId);
      return;
    }

    const config = this.data?.chartConfig || {};
    const configHeight =
      this.data?.chartHeight ||
      config.chartHeight ||
      config.chartheight ||
      config.height;
    if (configHeight) {
      chartDom.style.height = String(configHeight);
    }

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom, 'dvm');
    }
    this.myChart.showLoading();

    if (this.data?.data) {
      this.initChart(this.data.data);
      return;
    }

    if (!this.data?.apiUrl) {
      this.myChart.hideLoading();
      this.showNoDataMessage();
      console.warn('No data or apiUrl provided for widget:', this.data?.widgetId);
      return;
    }

    this.configService.getConfig(this.data.apiUrl, this.data, filters).subscribe({
      next: (res) => {
        const chartData = res?.data || res;
        this.initChart(chartData);
      },
      error: (err) => {
        console.error(`[MbarMline] API error for ${this.data?.widgetId}:`, err);
        this.showNoDataMessage();
      },
    });

    this.addResizeListener();
  }

  protected initChart(chartData: any): void {
    if (!chartData) {
      this.showNoDataMessage();
      return;
    }

    this.lastChartData = chartData;

    const theme = this.themeService.resolve(this.data?.themeOverrides);
    const config = this.data?.chartConfig || {};
    // Font scale for large / TV screens
    const fs = this.responsiveLayoutService.getFontScale();
    const sc = (px: number) => Math.round(px * fs);
    const normalized = this.normalizeInput(chartData);
    this.metaInfo = normalized.metaInfo;
    if (!this.hasRenderableData(normalized)) {
      this.showNoDataMessage();
      return;
    }

    const barMetric = this.data?.barValueField || config.barValueField || 'gross_value';
    const rawLineMetric = this.data?.lineValueField ?? config.lineValueField ?? 'percentage';
    const lineMetric = String(rawLineMetric || '').trim();
    const dimensionField = this.data?.dimensionField || config.dimensionField || 'year';
    const lineSeriesExplicitlyDisabled =
      config.showLines === false ||
      lineMetric.length === 0 ||
      (Array.isArray(config.lineYears) && config.lineYears.length === 0);

    const useSingleMetricSeries =
      normalized.isFlatArrayFormat &&
      (config.useSingleMetricSeries ?? this.data?.useSingleMetricSeries ?? true);

    const barSeries = this.resolveSeriesConfig(
      config.barSeries || [],
      config.barYears,
      'bar',
      barMetric,
      dimensionField,
      config.barNamePrefix || '',
    );
    const lineSeries = lineSeriesExplicitlyDisabled
      ? []
      : this.resolveSeriesConfig(
          config.lineSeries || [],
          config.lineYears,
          'line',
          lineMetric,
          dimensionField,
          config.lineNamePrefix || 'Margin ',
        );

    const fallbackYears = this.resolveUniqueDimensionValues(
      normalized.recordsByCategory,
      dimensionField,
    ).sort((a, b) => Number(b) - Number(a));

    const finalBars = useSingleMetricSeries && !barSeries.length
      ? [
          {
            mode: 'bar' as const,
            name: String(config.barSeriesName || this.data?.barSeriesName || 'Total'),
            valueField: barMetric,
            dimensionField: '__all__',
            dimensionValue: '__all__',
            yAxisIndex: 0,
          },
        ]
      : barSeries.length
        ? barSeries
        : fallbackYears.slice(0, 3).map((year) => ({
            mode: 'bar' as const,
            name: String(year),
            valueField: barMetric,
            dimensionField,
            dimensionValue: year,
            yAxisIndex: 0,
          }));

    const finalLines = lineSeriesExplicitlyDisabled
      ? []
      : useSingleMetricSeries && !lineSeries.length
        ? [
            {
              mode: 'line' as const,
              name: String(config.lineSeriesName || this.data?.lineSeriesName || 'Margin %'),
              valueField: lineMetric,
              dimensionField: '__all__',
              dimensionValue: '__all__',
              yAxisIndex: 1,
            },
          ]
        : lineSeries.length
          ? lineSeries
          : fallbackYears.slice(0, 3).map((year) => ({
              mode: 'line' as const,
              name: `Margin ${year}`,
              valueField: lineMetric,
              dimensionField,
              dimensionValue: year,
              yAxisIndex: 1,
            }));

    const seriesConfig: MixedSeriesConfig[] = [...finalBars, ...finalLines];
    const categories = normalized.categories;
    this.allCategories = categories;
    const enableQuarterYearAxis = config.enableQuarterYearAxis === true;
    const quarterAxisValues = categories.map(
      (category) => normalized.categoryMetaByCategory?.[category]?.quarter || category,
    );
    const yearAxisRawValues = categories.map(
      (category) => normalized.categoryMetaByCategory?.[category]?.year || '',
    );
    const yearAxisValues = yearAxisRawValues.map((year, idx) =>
      idx === 0 || year !== yearAxisRawValues[idx - 1] ? year : '',
    );

    const colorPalette: string[] =
      config.colors ||
      this.data?.colors ||
      ['#9e9e9e', '#000000', '#e53935', '#8b0000', '#16a34a', '#1e88e5', '#6b7280', '#ef4444'];

    const barScale = Number(config.barScale || this.data?.barScale || 1000000);
    const barSuffix = String(config.barSuffix || this.data?.barSuffix || 'M');
    const lineSuffix = String(config.lineSuffix || this.data?.lineSuffix || '%');
    const lineDecimals = Number(config.lineDecimals ?? this.data?.lineDecimals ?? 0);

    const series = seriesConfig.map((item, idx) => {
      const dataPoints = categories.map((category) => {
        const rows = normalized.recordsByCategory[category] || [];
        const row =
          item.dimensionField === '__all__'
            ? rows[0]
            : rows.find(
                (r: any) => String(r?.[item.dimensionField]) === String(item.dimensionValue),
              );
        const rawValue = Number(row?.[item.valueField] ?? 0);
        const chartValue =
          item.mode === 'bar' ? rawValue / (barScale || 1) : Number(rawValue.toFixed(lineDecimals));

        return {
          value: chartValue,
          rawValue,
          category,
          quarter: normalized.categoryMetaByCategory?.[category]?.quarter,
          year: normalized.categoryMetaByCategory?.[category]?.year,
          [item.dimensionField]: item.dimensionValue,
          metric: item.valueField,
          series: item.name,
        };
      });

      if (item.mode === 'bar') {
        return {
          name: item.name,
          type: 'bar',
          yAxisIndex: item.yAxisIndex ?? 0,
          xAxisIndex: 0,
          itemStyle: { color: colorPalette[idx % colorPalette.length] },
          barMaxWidth: sc(config.barMaxWidth || 22),
          label:
            config.showBarLabels === false
              ? { show: false }
              : {
                  show: true,
                  position: 'top',
                  formatter: (params: any) => `${Math.round(params.value)}${barSuffix}`,
                  fontSize: sc(10),
                  color: theme.chart?.dataLabelOutside || theme.chart?.textColor || '#444',
                  textBorderColor: 'transparent',
                  textBorderWidth: 0,
                  textShadowBlur: 0,
                  textShadowColor: 'transparent',
                },
          data: dataPoints,
        };
      }

      return {
        name: item.name,
        type: 'line',
        yAxisIndex: item.yAxisIndex ?? 1,
        xAxisIndex: 0,
        itemStyle: { color: colorPalette[idx % colorPalette.length] },
        symbol: config.lineSymbol || 'circle',
        symbolSize: sc(config.lineSymbolSize || 6),
        lineStyle: { width: sc(config.lineWidth || 2) },
        label:
          config.showLineLabels === false
            ? { show: false }
            : {
                show: true,
                position: 'top',
                formatter: (params: any) => `${Number(params.value).toFixed(lineDecimals)}${lineSuffix}`,
                fontSize: sc(10),
                color: theme.chart?.dataLabelOutside || theme.chart?.textColor || '#444',
                textBorderColor: 'transparent',
                textBorderWidth: 0,
                textShadowBlur: 0,
                textShadowColor: 'transparent',
              },
        data: dataPoints,
      };
    });
    if (!series.length) {
      this.showNoDataMessage();
      return;
    }

    const option: echarts.EChartsCoreOption = {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: theme.tooltip?.bg || '#fff',
        borderColor: theme.tooltip?.borderColor || '#ccc',
        textStyle: {
          color: theme.tooltip?.textColor || '#333',
          fontSize: theme.tooltip?.fontSize || sc(12),
        },
        formatter: (items: any) => {
          const rows = Array.isArray(items) ? items : [items];
          const title = rows[0]?.axisValueLabel || rows[0]?.name || '';
          const body = rows
            .map((point: any) => {
              const rawValue = point?.data?.rawValue ?? point?.value ?? 0;
              const valueText =
                point.seriesType === 'line'
                  ? `${Number(rawValue).toFixed(lineDecimals)}${lineSuffix}`
                  : `${Math.round(Number(rawValue) / (barScale || 1))}${barSuffix}`;
              return `${point.marker} <b>${point.seriesName}:</b> ${valueText}`;
            })
            .join('<br/>');
          return `<div style="text-align:left;"><b>${title}</b><br/>${body}</div>`;
        },
      },
      legend: {
        top: sc(4),
        left: 'center',
        type: 'scroll',
        data: seriesConfig.map((s) => s.name),
        textStyle: { fontSize: sc(11) },
        itemWidth: sc(14),
        itemHeight: sc(10),
      },
      grid: {
        top: sc(config.gridTop || 60),
        left: sc(config.gridLeft || 40),
        right: sc(config.gridRight || 48),
        bottom: sc(config.gridBottom || (enableQuarterYearAxis ? 92 : 72)),
        containLabel: true,
      },
      xAxis: enableQuarterYearAxis
        ? [
            {
              type: 'category',
              data: quarterAxisValues,
              triggerEvent: true,
              axisTick: { alignWithLabel: true },
              axisLabel: {
                interval: 0,
                rotate: config.xAxisRotate ?? 0,
                fontSize: sc(11),
              },
            },
            {
              type: 'category',
              position: 'bottom',
              offset: sc(config.yearAxisOffset ?? 22),
              data: yearAxisValues,
              triggerEvent: true,
              axisTick: { show: false },
              axisLine: { show: false },
              axisLabel: {
                interval: 0,
                rotate: 0,
                fontSize: sc(11),
                color: theme.chart?.axisLabelColor || '#374151',
                fontWeight: 600,
              },
            },
          ]
        : {
            type: 'category',
            data: categories,
            triggerEvent: true,
            axisLabel: {
              interval: 0,
              rotate: config.xAxisRotate ?? 45,
              fontSize: sc(11),
            },
          },
      yAxis: [
        {
          type: 'value',
          name: config.leftAxisName || `Sales ($${barSuffix})`,
          nameTextStyle: { fontSize: sc(12), color: theme.chart?.axisLabelColor || '#333' },
          triggerEvent: true,
          min: 0,
          axisLabel: { fontSize: sc(10), formatter: `{value}${barSuffix}`, color: theme.chart?.axisLabelColor },
          splitLine: { show: false },
        },
        {
          type: 'value',
          name: lineSeriesExplicitlyDisabled ? '' : (config.rightAxisName || 'Margin %'),
          nameTextStyle: { fontSize: sc(12), color: theme.chart?.axisLabelColor || '#333' },
          triggerEvent: true,
          min: config.rightAxisMin ?? 0,
          max: config.rightAxisMax ?? 100,
          axisLabel: { fontSize: sc(10), formatter: `{value}${lineSuffix}`, color: theme.chart?.axisLabelColor },
          splitLine: { show: false },
          show: !lineSeriesExplicitlyDisabled,
        },
      ],
      series: series as any,
    };

    this.myChart.setOption(option, true);
    this.myChart.hideLoading();
    this.setupChartClickEvent();

    // Re-apply active visual selection after re-render
    if (this.selectedCategories.length > 0) {
      this.highlightCategories(this.selectedCategories);
    } else if (this.selectedLegends.length > 0) {
      this.highlightSeriesByNames(this.selectedLegends);
    }
  }

  protected hasRenderableData(normalized: NormalizedInputResult): boolean {
    if (!Array.isArray(normalized.categories) || normalized.categories.length === 0) {
      return false;
    }

    return Object.values(normalized.recordsByCategory || {}).some(
      (rows: any[]) => Array.isArray(rows) && rows.length > 0,
    );
  }

  protected showNoDataMessage(): void {
    if (!this.myChart) return;

    const config = this.data?.chartConfig || {};
    const noDataText = String(
      this.data?.noDataText || config.noDataText || 'No data is available. Please try changing filters.',
    );

    this.myChart.off('click');
    this.myChart.clear();
    this.myChart.setOption(
      {
        animation: false,
        xAxis: { show: false, type: 'category', data: [] },
        yAxis: { show: false, type: 'value' },
        series: [],
        graphic: {
          type: 'text',
          left: 'center',
          top: 'middle',
          silent: true,
          style: {
            text: noDataText,
            fill: config.noDataTextColor || '#6b7280',
            fontSize: Number(config.noDataFontSize ?? 14),
            fontWeight: config.noDataFontWeight || 500,
            textAlign: 'center',
            textVerticalAlign: 'middle',
          },
        },
      },
      true,
    );
    this.myChart.hideLoading();
  }

  protected setupChartClickEvent(): void {
    if (!this.myChart) return;
    this.myChart.off('click');
    this.myChart.on('click', (params: any) => {
      // Handle x-axis label clicks
      if (params?.componentType === 'xAxis') {
        if (this.data?.enableLabelClick) {
          const clickedCategory = params?.value || this.allCategories[params?.dataIndex] || '';
          if (clickedCategory) {
            this.handleLabelClick(String(clickedCategory));
          }
        }
        return;
      }

      // Handle y-axis label clicks
      if (params?.componentType === 'yAxis') {
        const axisIndex = params.yAxisIndex ?? 0;
        if (axisIndex === 1 && this.data?.enableDoubleYAxis && this.data?.enableYAxis2Click) {
          this.chartFilterHandler.handleDoubleYAxisClick(this.data, params.value);
          this.loadAndRenderChart(this.globalFilterService.getCurrentFilters());
        } else if (axisIndex === 0 && this.data?.enableDoubleXAxis && this.data?.enableXAxis2Click) {
          this.chartFilterHandler.handleDoubleXAxisClick(this.data, params.value);
          this.loadAndRenderChart(this.globalFilterService.getCurrentFilters());
        }
        return;
      }

      // Bar/line click → treat as label click
      if (this.data?.enableLabelClick) {
        const clickedCategory = params?.data?.category ?? params?.name;
        if (clickedCategory) {
          this.handleLabelClick(String(clickedCategory));
        }
        return;
      }

      // Fallback: original filter selection behavior
      if (!this.data?.filterKey) return;

      const filterKeys: string[] = Array.isArray(this.data.filterKey)
        ? this.data.filterKey
        : String(this.data.filterKey)
            .split(',')
            .map((k: string) => k.trim())
            .filter(Boolean);

      const payload: any = {};
      const categoryKey = this.data?.categoryKey || this.data?.xAxis || 'category';
      const dimensionField = this.data?.dimensionField || this.data?.chartConfig?.dimensionField || 'year';
      const seriesKey = this.data?.seriesKey || 'series';
      const qtrKeys = new Set(['qtr', 'quarter']);

      filterKeys.forEach((key: string) => {
        if (params?.data && typeof params.data === 'object' && key in params.data) {
          payload[key] = params.data[key];
          return;
        }
        if (key === categoryKey || key === 'category') {
          payload[key] = params?.data?.category ?? params?.name;
          return;
        }
        if (qtrKeys.has(String(key).toLowerCase())) {
          payload[key] = params?.data?.quarter;
          return;
        }
        if (key === dimensionField || key === 'year') {
          payload[key] = params?.data?.year ?? params?.data?.[dimensionField] ?? this.extractYear(params?.seriesName);
          return;
        }
        if (key === seriesKey || key === 'series') {
          payload[key] = params?.seriesName;
        }
      });

      if (!Object.keys(payload).length) return;
      this.chartFilterHandler.updateFilterSelection(this.data, payload);
      this.loadAndRenderChart(this.globalFilterService.getCurrentFilters());
    });

    // Handle ECharts built-in legend click
    this.myChart.off('legendselectchanged');
    this.myChart.on('legendselectchanged', (params: any) => {
      if (!this.data?.enableLegendClick) return;
      // Prevent ECharts default legend toggle
      const selected: any = {};
      Object.keys(params.selected).forEach(k => selected[k] = true);
      this.myChart.setOption({ legend: { selected } });

      const clickedName = params.name;

      // Multi-select toggle for legend
      if (this.data?.isMultiSelect) {
        const idx = this.selectedLegends.indexOf(clickedName);
        if (idx === -1) {
          this.selectedLegends = [...this.selectedLegends, clickedName];
        } else {
          this.selectedLegends = this.selectedLegends.filter(l => l !== clickedName);
        }
      } else {
        if (this.selectedLegends.length === 1 && this.selectedLegends[0] === clickedName) {
          this.selectedLegends = [];
        } else {
          this.selectedLegends = [clickedName];
        }
      }

      // Visual highlighting
      if (this.selectedLegends.length === 0) {
        this.resetSeriesHighlight();
      } else {
        this.highlightSeriesByNames(this.selectedLegends);
      }

      // Broadcast filter to other charts
      const legendKey = this.data?.legendKey || this.data?.filterKey;
      const listenerConfig =
        this.data?.legendListener ||
        this.data?.filterListener ||
        this.data?.filterListner;

      if (legendKey) {
        if (this.selectedLegends.length > 0) {
          this.globalFilterService.updateFilterSelection(
            legendKey, this.selectedLegends, false, this.data.widgetId, true, listenerConfig,
          );
        } else {
          this.globalFilterService.resetFilterSelection(legendKey, false, listenerConfig);
        }
        this.loadAndRenderChart(this.globalFilterService.getCurrentFilters());
      }
    });
  }

  // ==================== CROSS-FILTERING METHODS ====================

  protected handleLabelClick(clickedCategory: string): void {
    if (!clickedCategory || !this.data?.enableLabelClick) return;
    const clickedStr = String(clickedCategory);
    const isMultiSelect = this.data?.isMultiSelect === true;

    if (isMultiSelect) {
      const idx = this.selectedCategories.findIndex(c => String(c) === clickedStr);
      if (idx === -1) {
        this.selectedCategories = [...this.selectedCategories, clickedStr];
      } else {
        this.selectedCategories = this.selectedCategories.filter(c => String(c) !== clickedStr);
      }
    } else {
      if (this.selectedCategories.length === 1 && String(this.selectedCategories[0]) === clickedStr) {
        this.selectedCategories = [];
      } else {
        this.selectedCategories = [clickedStr];
      }
    }

    if (this.selectedCategories.length === 0) {
      this.resetCategoryHighlight();
      this.broadcastCategoryFilter([]);
    } else {
      this.highlightCategories(this.selectedCategories);
      this.broadcastCategoryFilter(this.selectedCategories);
    }
  }

  protected broadcastCategoryFilter(categories: string[]): void {
    const listenerConfig =
      this.data?.labelListener ||
      this.data?.legendListener ||
      this.data?.filterListener ||
      this.data?.filterListner;

    const labelKey = this.data?.labelKey || this.data?.filterKey;

    // Step 1: legendSync for visual dimming on other charts
    const legendSyncData = {
      ...this.globalFilterService.getCurrentFilters(),
      ...buildCategoryFilterData(categories, this.data.widgetId, listenerConfig, labelKey),
    };
    (this.globalFilterService as any).filterSubject.next(legendSyncData);

    // Step 2: regular filter update for API calls
    if (labelKey) {
      if (categories.length > 0) {
        this.globalFilterService.updateFilterSelection(
          labelKey, categories, false, this.data.widgetId, true, listenerConfig,
        );
      } else {
        this.globalFilterService.resetFilterSelection(labelKey, false, listenerConfig);
      }
    }

    // Step 3: reload self
    this.loadAndRenderChart(this.globalFilterService.getCurrentFilters());
  }

  protected handleExternalLegendSync(filters: any): void {
    if (filters.sourceWidgetId === this.data?.widgetId) return;
    if (!this.myChart) return;

    if (filters.selectedLegends?.length === 0 && filters.selectedIndices?.length === 0) {
      this.selectedCategories = [];
      this.resetCategoryHighlight();
      return;
    }

    if (filters.selectedLegends?.length > 0) {
      const matchMode = (this.data?.chartConfig?.categoryMatchMode || 'contains') as MatchMode;
      const matchingCategories = this.allCategories.filter((cat: string) =>
        filters.selectedLegends.some((legend: string) => matchValue(cat, legend, matchMode))
      );
      if (matchingCategories.length > 0) {
        this.selectedCategories = matchingCategories;
        this.highlightCategories(matchingCategories);
      }
    }
  }

  protected highlightCategories(categories: string[]): void {
    if (!this.myChart) return;
    const option = this.myChart.getOption() as any;
    if (!option?.series) return;

    const categoryIndices = categories
      .map((cat: string) => this.allCategories.findIndex(c => String(c) === String(cat)))
      .filter((i: number) => i !== -1);

    const updatedSeries = option.series.map((s: any) => {
      if (!Array.isArray(s.data)) return s;
      const updatedData = s.data.map((val: any, i: number) => {
        const isSelected = categoryIndices.includes(i);
        const rawValue = typeof val === 'object' ? (val?.value ?? val) : val;
        return {
          ...(typeof val === 'object' ? val : {}),
          value: rawValue,
          itemStyle: { ...(val?.itemStyle || {}), opacity: isSelected ? 1 : 0.08 },
          label: { ...(val?.label || {}), opacity: isSelected ? 1 : 0 },
        };
      });
      return { ...s, data: updatedData };
    });

    this.myChart.setOption({ series: updatedSeries }, false);
  }

  protected resetCategoryHighlight(): void {
    this.selectedCategories = [];
    if (!this.myChart) return;
    const option = this.myChart.getOption() as any;
    if (!option?.series) return;

    const updatedSeries = option.series.map((s: any) => {
      if (!Array.isArray(s.data)) return s;
      const updatedData = s.data.map((val: any) => {
        const rawValue = typeof val === 'object' ? (val?.value ?? val) : val;
        return {
          ...(typeof val === 'object' ? val : {}),
          value: rawValue,
          itemStyle: { ...(val?.itemStyle || {}), opacity: 1 },
          label: { ...(val?.label || {}), opacity: 1 },
        };
      });
      return { ...s, data: updatedData };
    });

    this.myChart.setOption({ series: updatedSeries }, false);
  }

  protected highlightSeriesByNames(names: string[]): void {
    if (!this.myChart) return;
    const option = this.myChart.getOption() as any;
    if (!option?.series) return;

    const updatedSeries = option.series.map((s: any) => {
      const seriesName = Array.isArray(s.name) ? s.name[0] : s.name;
      const seriesType = Array.isArray(s.type) ? s.type[0] : s.type;
      const isMatch = names.includes(seriesName);
      const opacity = isMatch ? 1 : 0.1;

      if (!Array.isArray(s.data)) {
        return { ...s, itemStyle: { ...(Array.isArray(s.itemStyle) ? s.itemStyle[0] : s.itemStyle), opacity } };
      }

      const updatedData = s.data.map((val: any) => {
        const rawValue = typeof val === 'object' ? (val?.value ?? val) : val;
        return {
          ...(typeof val === 'object' ? val : {}),
          value: rawValue,
          itemStyle: { ...(val?.itemStyle || {}), opacity },
          label: { ...(val?.label || {}), opacity: isMatch ? 1 : 0 },
        };
      });

      return {
        ...s,
        itemStyle: { ...(Array.isArray(s.itemStyle) ? s.itemStyle[0] : s.itemStyle), opacity },
        lineStyle: seriesType === 'line' ? { ...(Array.isArray(s.lineStyle) ? s.lineStyle[0] : s.lineStyle), opacity } : s.lineStyle,
        data: updatedData,
      };
    });

    this.myChart.setOption({ series: updatedSeries }, false);
  }

  protected resetSeriesHighlight(): void {
    if (!this.myChart) return;
    const option = this.myChart.getOption() as any;
    if (!option?.series) return;

    const updatedSeries = option.series.map((s: any) => {
      if (!Array.isArray(s.data)) {
        return { ...s, itemStyle: { ...(Array.isArray(s.itemStyle) ? s.itemStyle[0] : s.itemStyle), opacity: 1 } };
      }

      const updatedData = s.data.map((val: any) => {
        const rawValue = typeof val === 'object' ? (val?.value ?? val) : val;
        return {
          ...(typeof val === 'object' ? val : {}),
          value: rawValue,
          itemStyle: { ...(val?.itemStyle || {}), opacity: 1 },
          label: { ...(val?.label || {}), opacity: 1 },
        };
      });

      return {
        ...s,
        itemStyle: { ...(Array.isArray(s.itemStyle) ? s.itemStyle[0] : s.itemStyle), opacity: 1 },
        lineStyle: { ...(Array.isArray(s.lineStyle) ? s.lineStyle[0] : s.lineStyle), opacity: 1 },
        data: updatedData,
      };
    });

    this.myChart.setOption({ series: updatedSeries }, false);
  }

  protected extractYear(input: any): string {
    const match = String(input || '').match(/(19|20)\d{2}/);
    return match ? match[0] : '';
  }

  protected normalizeInput(raw: any): NormalizedInputResult {
    const config = this.data?.chartConfig || {};
    const enableFlatArrayResponse =
      (config.enableFlatArrayResponse ?? this.data?.enableFlatArrayResponse) === true;
    const dataKey = this.data?.dataKey || config.dataKey;
    const categoryFields: string[] = this.data?.categoryFields || config.categoryFields || ['quarter', 'year'];
    const categoryDelimiter: string = this.data?.categoryDelimiter ?? config.categoryDelimiter ?? ' ';
    const quarterField = this.data?.quarterField || config.quarterField || 'quarter';
    const yearField = this.data?.yearField || config.yearField || 'year';

    const data = raw?.data || raw;
    const metaInfo = {
      totalRecords: Number(data?.totalRecords),
      totalPages: Number(data?.totalPages),
      currentPage: Number(data?.currentPage),
      pageSize: Number(data?.pageSize),
    };

    if (enableFlatArrayResponse) {
      const flatData: any[] = Array.isArray(data)
        ? data
        : dataKey && Array.isArray(data?.[dataKey])
          ? data[dataKey]
          : [];

      if (flatData.length > 0) {
        const buildCategory = (row: any): string =>
          categoryFields.map((field) => String(row?.[field] ?? '')).join(categoryDelimiter).trim();

        const quarterOrder = (value: any): number => {
          const match = String(value ?? '').toUpperCase().match(/Q\s*([1-4])/);
          return match ? Number(match[1]) : 0;
        };

        const uniqueCategories = Array.from(new Set(flatData.map((row: any) => buildCategory(row))));
        const categories = uniqueCategories.sort((a: string, b: string) => {
          const rowA = flatData.find((row: any) => buildCategory(row) === a) || {};
          const rowB = flatData.find((row: any) => buildCategory(row) === b) || {};
          const yearA = Number(rowA?.[yearField] ?? 0);
          const yearB = Number(rowB?.[yearField] ?? 0);
          if (yearA !== yearB) return yearA - yearB;
          return quarterOrder(rowA?.[quarterField]) - quarterOrder(rowB?.[quarterField]);
        });

        const recordsByCategory: Record<string, any[]> = {};
        const categoryMetaByCategory: Record<string, { quarter: string; year: string }> = {};
        categories.forEach((category) => {
          const rows = flatData.filter((row: any) => buildCategory(row) === category);
          recordsByCategory[category] = rows;
          const firstRow = rows[0] || {};
          categoryMetaByCategory[category] = {
            quarter: String(firstRow?.[quarterField] ?? ''),
            year: String(firstRow?.[yearField] ?? ''),
          };
        });

        return {
          categories,
          recordsByCategory,
          categoryMetaByCategory,
          isFlatArrayFormat: true,
          metaInfo,
        };
      }
    }

    if (data && typeof data === 'object' && !Array.isArray(data) && !data.category && !data.series) {
      const categories = Object.keys(data).filter((key) => Array.isArray(data[key]));
      const recordsByCategory: Record<string, any[]> = {};
      const categoryMetaByCategory: Record<string, { quarter: string; year: string }> = {};
      categories.forEach((cat) => {
        recordsByCategory[cat] = Array.isArray(data[cat]) ? data[cat] : [];
        const matchYear = String(cat).match(/(19|20)\d{2}/);
        const matchQuarter = String(cat).match(/Q\s*[1-4]/i);
        categoryMetaByCategory[cat] = {
          quarter: matchQuarter ? matchQuarter[0].replace(/\s+/g, '').toUpperCase() : '',
          year: matchYear ? matchYear[0] : '',
        };
      });
      return { categories, recordsByCategory, categoryMetaByCategory, isFlatArrayFormat: false, metaInfo };
    }

    if (data?.category && data?.series) {
      const categories = Array.isArray(data.category) ? data.category : [];
      const recordsByCategory: Record<string, any[]> = {};
      const categoryMetaByCategory: Record<string, { quarter: string; year: string }> = {};
      categories.forEach((cat: string, idx: number) => {
        recordsByCategory[cat] = (data.series || []).map((series: any) => ({
          year: series?.name,
          value: Array.isArray(series?.data) ? series.data[idx] : 0,
        }));
        const matchYear = String(cat).match(/(19|20)\d{2}/);
        const matchQuarter = String(cat).match(/Q\s*[1-4]/i);
        categoryMetaByCategory[cat] = {
          quarter: matchQuarter ? matchQuarter[0].replace(/\s+/g, '').toUpperCase() : '',
          year: matchYear ? matchYear[0] : '',
        };
      });
      return { categories, recordsByCategory, categoryMetaByCategory, isFlatArrayFormat: false, metaInfo };
    }

    return {
      categories: [],
      recordsByCategory: {},
      categoryMetaByCategory: {},
      isFlatArrayFormat: false,
      metaInfo,
    };
  }

  protected resolveSeriesConfig(
    explicitSeries: any[],
    yearList: Array<string | number>,
    mode: SeriesMode,
    defaultValueField: string,
    defaultDimensionField: string,
    namePrefix: string,
  ): MixedSeriesConfig[] {
    if (Array.isArray(explicitSeries) && explicitSeries.length > 0) {
      return explicitSeries.map((item: any) => ({
        mode,
        name: String(item?.name || `${namePrefix}${item?.dimensionValue ?? ''}`),
        valueField: String(item?.valueField || defaultValueField),
        dimensionField: String(item?.dimensionField || defaultDimensionField),
        dimensionValue: item?.dimensionValue,
        yAxisIndex: mode === 'bar' ? 0 : 1,
      }));
    }

    if (Array.isArray(yearList) && yearList.length > 0) {
      return yearList.map((year) => ({
        mode,
        name: mode === 'bar' ? String(year) : `${namePrefix}${year}`,
        valueField: defaultValueField,
        dimensionField: defaultDimensionField,
        dimensionValue: year,
        yAxisIndex: mode === 'bar' ? 0 : 1,
      }));
    }

    return [];
  }

  protected resolveUniqueDimensionValues(
    recordsByCategory: Record<string, any[]>,
    dimensionField: string,
  ): Array<string | number> {
    const values = new Set<string | number>();
    Object.values(recordsByCategory).forEach((rows: any[]) => {
      rows.forEach((row: any) => {
        if (row?.[dimensionField] !== undefined && row?.[dimensionField] !== null) {
          values.add(row[dimensionField]);
        }
      });
    });
    return Array.from(values);
  }

  protected addResizeListener(): void {
    const chartDom = document.getElementById(this.data?.widgetId || '');
    if (!chartDom) return;

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    this.resizeObserver = new ResizeObserver(() => this.myChart?.resize());
    this.resizeObserver.observe(chartDom);
    window.addEventListener('resize', this.handleResize);
  }

  private handleResize = (): void => {
    this.myChart?.resize();
  };

  hasMetaValue(value: any): boolean {
    return value !== undefined && value !== null && Number.isFinite(Number(value));
  }

  ngOnDestroy(): void {
    this.filterSubscription?.unsubscribe();
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    window.removeEventListener('resize', this.handleResize);
    this.myChart?.dispose();
  }
}
